import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bed, Maximize, Layers, Home, Shield, TreePine, Award, Users } from "lucide-react";
import SEO from "@/components/SEO";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/d9iiix97_T1.jpeg";

const PRODUCTS = [
  {
    name: "T1",
    subtitle: "Loft avec mezzanine",
    image: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/bm9vithu_T1.png",
    link: "/produits/t1",
    specs: ["Banquette + mezzanine", "13 m\u00B2", "CLT 60 mm"],
  },
  {
    name: "T2",
    subtitle: "Studio familial avec mezzanine",
    image: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/lz3rr73o_ChatGPT%20Image%2025%20avr.%202026%20a%CC%80%2017_22_05.png",
    link: "/produits/t2",
    specs: ["4 couchages", "13 m\u00B2", "CLT 60 mm"],
  },
  {
    name: "T3",
    subtitle: "4 couchages plein pied",
    image: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/2bq6f9k3_T3.png",
    link: "/produits/t3",
    specs: ["4 couchages", "13 m\u00B2", "CLT 60 mm"],
  },
];

const VALUES = [
  { icon: TreePine, title: "Bois massif CLT", text: "Des matériaux biosourcés pour dès habitats durables et sains." },
  { icon: Home, title: "Design architectural", text: "Une esthétique sobre et intemporelle, signee YONO." },
  { icon: Shield, title: "Qualité de fabrication", text: "Prefabrication en atelier, contrôle qualité rigoureux." },
  { icon: Award, title: "Prêt à vivre", text: "Livraison clé en main, raccordement simplifié." },
];

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <SEO title="Habitats bois contemporains — Modules bois haut de gamme" description="YONO conçoit des habitats bois contemporains, déplaçables et haut de gamme. Architecture sobre, matériaux biosourcés, fabrication française. T1, T2, T3 — prix sur demande." path="/" />

      {/* ═══ HERO ═══ */}
      {/* Desktop: video background + text overlay */}
      <section className="hidden md:block relative h-screen min-h-[600px] max-h-[900px]">
        <video src="https://customer-assets.emergentagent.com/job_yono-direct/artifacts/pmwm0fr8_video.mp4" autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" ref={(el) => { if (el) el.playbackRate = 0.75; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-black/5" />
        <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-16 md:pb-24">
          <div className="max-w-3xl">
            <p className="font-body text-lg md:text-xl text-white/90 mb-3 max-w-xl leading-relaxed font-medium">
              Créer un nouvel espace de vie, en bois
            </p>
            <p className="font-body text-base md:text-lg text-white/75 max-w-lg mb-10 leading-relaxed">
              YONO conçoit des habitats bois compacts et haut de gamme, prêts à vivre, pour créer une chambre, un bureau, un studio indépendant ou un espace invité. Architecture sobre, matériaux biosourcés et fabrication française.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/produits" data-testid="home-discover-btn"
                className="flex items-center gap-2 bg-white text-soft-black px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood hover:text-white transition-colors duration-300">
                Découvrir la gamme <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/contact" data-testid="home-contact-btn"
                className="flex items-center gap-2 border border-white/40 text-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-white/10 transition-colors duration-300">
                Nous contacter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile: video + text stacked */}
      <section className="md:hidden pt-16 -mt-[1px]">
        <div className="w-full overflow-hidden" style={{maxHeight: "65vh"}}>
          <video src="/video_mobile.mp4" autoPlay muted loop playsInline webkit-playsinline="true" className="w-full scale-[1.15] object-cover" ref={(el) => { if (el) el.playbackRate = 0.75; }} />
        </div>
        <div className="px-6 py-6">
          <p className="font-body text-lg text-soft-black mb-3 font-medium leading-relaxed">
            Créer un nouvel espace de vie, en bois
          </p>
          <p className="font-body text-base text-soft-black/70 mb-8 leading-relaxed">
            YONO conçoit des habitats bois compacts et haut de gamme, prêts à vivre, pour créer une chambre, un bureau, un studio indépendant ou un espace invité. Architecture sobre, matériaux biosourcés et fabrication française.
          </p>
          <div className="flex flex-col gap-3">
            <Link to="/produits" data-testid="home-discover-btn-mobile"
              className="flex items-center justify-center gap-2 bg-soft-black text-off-white px-6 py-4 font-body text-xs uppercase tracking-[0.15em]">
              Découvrir la gamme <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link to="/contact" data-testid="home-contact-btn-mobile"
              className="flex items-center justify-center gap-2 border border-soft-black text-soft-black px-6 py-4 font-body text-xs uppercase tracking-[0.15em]">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ BRAND STATEMENT ═══ */}
      <section className="py-8 md:py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">La marque</span>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-soft-black mt-4 mb-8 leading-tight">
              Des habitats compacts, chaleureux et durables
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="font-body text-base md:text-lg text-soft-black/60 leading-relaxed">
              YONO imagine des lieux de vie indépendants, compacts et déplaçables, conçus pour offrir du confort, de la lumière et une vraie sensation d'espace. Prêts à vivre et installables en quelques jours, ils permettent de créer une chambre en plus, d'accueillir famille et amis ou de développer une offre de location courte durée.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ PRODUCT SHOWCASE ═══ */}
      <section className="py-8 md:py-12 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">La gamme</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-soft-black mt-2 mb-2">
              Trois variantes, une même exigence
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 120}>
                <Link to={p.link} className="group block h-full">
                  <div className="overflow-hidden">
                    <img src={p.image} alt={`YONO ${p.name}`} className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="pt-4 pb-5 px-4 border-t border-warm-grey/20">
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-natural-wood">YONO</span>
                    <h3 className="font-heading text-3xl font-bold tracking-tight text-soft-black mt-1 mb-1">{p.name}</h3>
                    <p className="font-body text-sm text-soft-black/50 mb-2">{p.subtitle}</p>
                    <div className="flex items-center gap-4 mb-3">
                      {[Bed, Maximize, Layers].map((Icon, j) => (
                        <div key={j} className="flex items-center gap-1.5">
                          <Icon className="h-3.5 w-3.5 text-natural-wood" strokeWidth={1.5} />
                          <span className="font-mono text-[10px] uppercase tracking-wider text-soft-black/40">{p.specs[j]}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <span data-testid={`home-${p.name.toLowerCase()}-price`} className="font-heading text-xl tracking-tight text-soft-black">Prix sur demande</span>
                      <Link to={p.link} className="flex items-center justify-center gap-2 bg-soft-black text-off-white px-5 py-2.5 font-body text-xs uppercase tracking-[0.1em] hover:bg-natural-wood transition-colors duration-300">
                        En savoir plus <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ USAGES ═══ */}
      <section className="py-10 md:py-14 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Les usages</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-soft-black mt-2 mb-6">
              Des usages simples, concrets et durables
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="font-body text-base text-soft-black/60 leading-relaxed max-w-3xl mb-8">
              Pensés comme de vraies pièces en plus, les studios YONO répondent aux usages du quotidien :
              créer un bureau au calme, recevoir famille et amis, aménager une chambre indépendante ou
              développer une offre de location saisonnière. Compacts, équipés et préfabriqués en atelier,
              ils permettent d'ajouter rapidement un nouvel espace de vie, sans engager une extension lourde.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-warm-grey/20 border border-warm-grey/20">
            {[
              { icon: Home, title: "Extension de maison", text: "Créer une chambre, un bureau, un studio où une piece indépendante dans le jardin, avec un habitat bois chaleureux, équipé et prêt à l'usage." },
              { icon: Users, title: "Accueil famille et invites", text: "Offrir un espace autonome et confortable pour recevoir, sans modifier l'organisation de la maison principale." },
              { icon: Award, title: "Location saisonnière", text: "Valoriser un terrain où un jardin avec un hébergement compact, différenciant et adapté aux courts séjours." },
            ].map((u, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="bg-off-white p-8 md:p-10 h-full">
                  <u.icon className="h-6 w-6 text-natural-wood mb-5" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg font-medium tracking-tight text-soft-black mb-3">{u.title}</h3>
                  <p className="font-body text-sm text-soft-black/60 leading-relaxed">{u.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ VALUES ═══ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-warm-grey/30 border border-warm-grey/30">
            {VALUES.map((v, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="bg-off-white p-8 md:p-10 h-full">
                  <v.icon className="h-6 w-6 text-natural-wood mb-6" strokeWidth={1.5} />
                  <h3 className="font-heading text-base font-medium tracking-tight text-soft-black mb-3">{v.title}</h3>
                  <p className="font-body text-sm text-soft-black/60 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-soft-black mb-6">
              Envie d'un habitat bois simple, beau et prêt à vivre ?
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="font-body text-soft-black/60 leading-relaxed mb-10 max-w-xl mx-auto">
              Découvrez notre gamme où contactez-nous pour échanger sur votre projet.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/produits" data-testid="home-cta-products-btn"
                className="inline-flex items-center justify-center gap-2 bg-soft-black text-off-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300">
                Voir la gamme <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <Link to="/contact" data-testid="home-cta-contact-btn"
                className="inline-flex items-center justify-center gap-2 border border-soft-black text-soft-black px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-soft-black hover:text-off-white transition-colors duration-300">
                Nous contacter
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
